import { useMemo, useState, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { Globe } from "lucide-react";
import { BrandedLoader } from "@/components/BrandedLoader";
import { DeviceMock } from "@/components/DeviceMock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PreviewFrame = ({ url, device }) => {
  const proxied = useMemo(() => {
    if (!url) return null;
    return `${BACKEND_URL}/api/proxy?url=${encodeURIComponent(url)}`;
  }, [url]);

  const [booting, setBooting] = useState(false);
  const startedAt = useRef(0);

  useEffect(() => {
    if (!proxied) { setBooting(false); return; }
    setBooting(true);
    startedAt.current = Date.now();
    const fallback = setTimeout(() => setBooting(false), 20000);
    return () => clearTimeout(fallback);
  }, [proxied]);

  const handleLoad = () => {
    const elapsed = Date.now() - startedAt.current;
    const extra = Math.max(1600 - elapsed, 700);
    setTimeout(() => setBooting(false), extra);
  };

  return (
    <div className="flex-1 w-full flex items-center justify-center overflow-auto p-6 md:p-10">
      {!url ? (
        <EmptyState />
      ) : (
        <DeviceMock device={device} testid="preview-frame-container">
          <AnimatePresence>
            {booting && <BrandedLoader key="loader" />}
          </AnimatePresence>
          <iframe
            key={proxied}
            src={proxied}
            title="website-preview"
            onLoad={handleLoad}
            className="w-full h-full bg-white"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-presentation"
            referrerPolicy="no-referrer"
            data-testid="preview-iframe"
          />
        </DeviceMock>
      )}
    </div>
  );
};

const EmptyState = () => (
  <div className="flex flex-col items-center text-center max-w-md" data-testid="preview-empty-state">
    <div className="h-14 w-14 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center mb-6">
      <Globe className="h-6 w-6 text-white/60" />
    </div>
    <h2 className="font-display text-2xl font-semibold tracking-tight text-white">
      Preview any website
    </h2>
    <p className="mt-3 text-sm text-white/50 leading-relaxed">
      Paste a URL above and load it instantly. Toggle between desktop and mobile,
      then save or share a clean link with your clients.
    </p>
  </div>
);
